/**
 * Module: screens/creator/_SocialLinksEditor
 * Role: Presentational editor for creator socials (FTR-1458). No network calls —
 *       parent screen (ProfileEditScreen) owns useQuery/useMutation and passes
 *       socials + callbacks down. Underscore-prefixed = private helper component,
 *       not a route target.
 *
 *       Product invariant (EC-8, verified-vs-claimed): a verified row shows
 *       follower_count + "проверено {date}"; a self-reported row shows "со слов
 *       автора" and NEVER renders follower_count, even if the backend sent one —
 *       we don't hand the brand a number we can't stand behind. A verified row
 *       with a NULL follower_count (backend didn't parse a number) omits the
 *       number entirely rather than print "0 подписчиков" — same rule, same reason.
 *
 * Uses: api/creator:CreatorSocial, SocialNetwork; steps/_ProfileChips:Chips;
 *       styles/tokens; components/atoms
 * Used by: screens/creator/ProfileEditScreen.tsx
 * Glossary: ai/glossary/buyer.md
 */
import { useState } from 'react';
import { P, S, T } from '../../styles/tokens';
import { Card, Ic, Input, PressBtn } from '../../components/atoms';
import type { CreatorSocial, SocialNetwork } from '../../api/creator';
import { Chips } from './steps/_ProfileChips';

const NETWORK_LABEL: Record<string, string> = {
  vk: 'ВКонтакте',
  tiktok: 'TikTok',
  telegram: 'Telegram',
  youtube: 'YouTube',
  instagram: 'Instagram',
};

/** Unknown network from the server falls back to the raw value — never crash, never hide the row. */
function networkLabel(network: string): string {
  return NETWORK_LABEL[network] ?? network;
}

function formatCheckedAt(checkedAt: string | null | undefined): string {
  if (!checkedAt) return '';
  const d = new Date(checkedAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function SocialRow({
  social,
  onRemove,
  busy,
}: {
  social: CreatorSocial;
  onRemove: () => void;
  busy: boolean;
}) {
  const isVerified = social.verified_source !== 'self_reported';
  const subtitle = isVerified
    ? social.follower_count != null
      ? `${social.follower_count.toLocaleString('ru-RU')} подписчиков · проверено ${formatCheckedAt(social.checked_at)}`
      : `проверено ${formatCheckedAt(social.checked_at)}`
    : 'со слов автора';

  return (
    <Card style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
      <div
        style={{
          width: 32, height: 32, borderRadius: 10, flexShrink: 0,
          background: isVerified ? 'rgba(45,138,78,0.12)' : P.mist,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Ic name={isVerified ? 'check' : 'clock'} size={16} color={isVerified ? P.success : P.muted} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ font: T.bodyText, color: P.ink }}>{networkLabel(social.network)}</div>
        <div style={{ font: T.footnote, color: S.secondaryLabel, marginTop: 2 }}>{subtitle}</div>
      </div>
      <button
        type="button"
        aria-label={`Удалить ${networkLabel(social.network)}`}
        onClick={onRemove}
        disabled={busy}
        style={{
          background: 'transparent', border: 'none', padding: 8,
          color: P.danger, flexShrink: 0,
          cursor: busy ? 'default' : 'pointer',
          opacity: busy ? 0.5 : 1,
        }}
      >
        <Ic name="cross" size={16} color={P.danger} />
      </button>
    </Card>
  );
}

export interface SocialLinksEditorProps {
  socials: CreatorSocial[];
  /** FTR-1458 Task 4: networks the creator can still add — server-computed (SSOT). */
  availableNetworks: string[];
  onAdd: (network: SocialNetwork, url: string) => void;
  onRemove: (network: SocialNetwork) => void;
  busy?: boolean;
  error?: string | null;
}

export function SocialLinksEditor({
  socials,
  availableNetworks,
  onAdd,
  onRemove,
  busy = false,
  error,
}: SocialLinksEditorProps) {
  const usedNetworks = new Set(socials.map((s) => s.network));
  const available = availableNetworks.filter((n) => !usedNetworks.has(n));
  const [network, setNetwork] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  // Derived, not a second piece of state: `available` shrinks after a successful add
  // (its old selection can vanish from the list) — re-deriving on every render keeps
  // the highlighted chip and handleAdd's target in sync instead of re-POSTing a
  // network that was just added (BUG class fixed by code review Blocker C).
  const selected = network && available.includes(network) ? network : (available[0] ?? null);

  const handleAdd = () => {
    if (!selected || url.trim() === '' || busy) return;
    onAdd(selected, url.trim());
    setUrl('');
  };

  return (
    <div>
      {socials.map((s) => (
        <SocialRow key={s.network} social={s} onRemove={() => onRemove(s.network)} busy={busy} />
      ))}

      {/* Hoisted above the `available.length > 0` guard (Blocker B): once every
          network is taken, this card doesn't render at all — a delete error would
          otherwise be set in state and never shown, exactly when delete is the
          only action left on the screen. */}
      {error && (
        <div style={{ font: T.footnote, color: P.danger, marginTop: 8, marginBottom: 8 }}>{error}</div>
      )}

      {available.length > 0 && (
        <Card>
          <div style={{ font: T.footnote, color: S.secondaryLabel, marginBottom: 8 }}>
            Добавить площадку
          </div>
          <Chips
            items={available.map((n) => ({ value: n, label: networkLabel(n) }))}
            selected={selected ? [selected] : []}
            onToggle={setNetwork}
          />
          <div style={{ marginTop: 12 }}>
            <Input
              value={url}
              onChange={setUrl}
              placeholder="Ссылка на профиль или канал"
            />
          </div>
          <div style={{ marginTop: 12 }}>
            <PressBtn size="md" onClick={handleAdd} disabled={busy || !selected || url.trim() === ''}>
              {busy ? 'Добавляем…' : 'Добавить'}
            </PressBtn>
          </div>
        </Card>
      )}
    </div>
  );
}
